import { Box, Button, IconButton } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { SwitchAccountBtn } from 'src/components';
import { CustomDashboardProvider } from 'src/contexts/customDashboardContext/customDashboard.context';
import { TENANT_SWITCH_PATH } from 'src/routes/config';
import UserAction from 'src/components/streametric/user-action/UserAction';
import { MainDashboard } from '../components/mainDashboard';
import { EditorMode } from '../types';
import { useEffect, useState } from 'react';
import { useResponsive } from 'src/hooks/useResponsive';
import { EditIcon } from 'src/assets/icons/EditIcon';
import { ListIcon } from 'src/assets/icons/ListIcon';
import { HeaderControllerBar } from '../components/headerControllerBar';
import { BaseHeader } from 'src/layouts/baseHeader';
import { DashboardService } from 'src/services/dashboard/dashboard.service';
import { useAuthentication } from 'src/features/authentication/context';
import { PreviewDashboardTitle } from '../components/previewDashboardTitle/PreviewDashboardTitle';
import { DeleteDashboardDialog } from '../components/deleteDashboardDialog/DeleteDashboardDialog';
import { useAllDashboardsContext } from 'src/contexts/allDashboardsContext/allDashboards.context';
import { ACCESS_ROLES } from 'src/config';
import { useFullscreen } from 'src/contexts/fullscreenContext';

export const PreviewDashboard = () => {
  const isMobile = useResponsive('down', 'md');
  const { isFullscreen } = useFullscreen();
  const {
    customerId: { value: customerId },
    getCurrentRole,
  } = useAuthentication();
  const { dashboardId } = useParams();
  const [mode, setMode] = useState<EditorMode>('preview');
  const [dashboard, setDashboard] = useState<any>(null); //Add type

  const isSuperAdmin = getCurrentRole(customerId, null) === ACCESS_ROLES.SUPER_ADMIN;
  const isAccountAdmin = getCurrentRole(customerId, null) === ACCESS_ROLES.ACCOUNT_ADMIN;

  const [isDeleteDialog, setIsDeleteDialog] = useState<boolean>(false);

  useEffect(() => {
    const getDashboard = async () => {
      const response = await DashboardService.getById({
        customerId: customerId!,
        dashboardId: dashboardId!,
      });

      setDashboard(response);
    };
    getDashboard();
  }, [customerId, dashboardId]);

  const onToggleMode = () => {
    setMode((prevMode) => (prevMode === 'preview' ? 'edit' : 'preview'));
  };
  const isEditMode = mode === 'edit';

  const handleClose = () => {
    setMode('preview');
  };

  const initialNodesData =
    dashboard?.nodes?.map((item: any) => ({
      ...item,
      selected: false,
      dragging: false,
    })) ?? [];

  const { onDeleteDashboard } = useAllDashboardsContext();

  return (
    <CustomDashboardProvider
      mode="edit"
      initialNodes={initialNodesData}
      initialTitle={dashboard?.title}
    >
      <>
        <DeleteDashboardDialog
          onConfirm={async () => {
            await onDeleteDashboard({ dashboardId: dashboardId, customerId: customerId });
            setIsDeleteDialog(false);
          }}
          isOpen={isDeleteDialog}
          onClose={() => setIsDeleteDialog(false)}
        />
        {isEditMode ? (
          <BaseHeader />
        ) : (
          <Box
            sx={{
              padding: isFullscreen
                ? isMobile
                  ? '12px 16px'
                  : '35px 24px'
                : isMobile
                ? '12px 0'
                : '35px 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: '32px',
                alignItems: 'center',
                marginLeft: isMobile ? '30px' : '0px',
              }}
            >
              <PreviewDashboardTitle />
              {!isMobile && (isAccountAdmin || isSuperAdmin) && (
                <Button
                  onClick={onToggleMode}
                  size="small"
                  variant="contained"
                  startIcon={<EditIcon width={15} height={15} />}
                >
                  Editor
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '40px' }}>
              {isMobile ? (
                <IconButton to={TENANT_SWITCH_PATH} component={Link}>
                  <ListIcon width={24} height={24} />
                </IconButton>
              ) : (
                <SwitchAccountBtn />
              )}
              <UserAction />
            </Box>
          </Box>
        )}

        {isEditMode && (
          <HeaderControllerBar
            sx={{ margin: '18px 0' }}
            setMode={setMode}
            handleClose={handleClose}
            isEdit
          />
        )}
        <MainDashboard mode={mode} />
        {(isAccountAdmin || isSuperAdmin) && isEditMode && (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '1.5rem 1.5rem 2rem 1.5rem',
            }}
          >
            <Button color="error" variant="outlined" onClick={() => setIsDeleteDialog(true)}>
              Delete Dashboard
            </Button>
          </Box>
        )}
      </>
    </CustomDashboardProvider>
  );
};
